"use client";
import { DayDate } from "@cmp-src/data-display/dates/DayDate";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { EditIcon } from "@cmp-src/icons/TacticIcons/EditIcon";
import { ViewDeskIcon } from "@cmp-src/icons/TacticIcons/VIewDeskIcon";
import { ISchedule, ISlot, WorkplaceRequestElement } from "@gettactic/api";
import { Button } from "@cmp-src/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@cmp-src/primitives/dropdown-menu";

import {
  useAreas,
  useAuthenticated,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import { useRouter } from "@/lib/useRouter";
import {
  isRoom,
  createMapLink,
  cn,
  findCheckinTargetSlot,
  useBooleanState,
  useDisclosure
} from "@gettactic/helpers";
import { useReserveModal } from "@/features/reserve/hooks/useReserveModal";
import { useMapModalContext } from "@/features/map/components/modal";
import { getMapModalTab } from "@/features/map/helpers/getMapModalTab";
import { getDuplicateReservationData } from "@/features/reserve/providers/getDuplicateReservationData";
import { getScheduleRooms } from "@gettactic/helpers/src/schedules/getScheduleRooms";
import { EventRoomTabs } from "./EventRoomTabs";
import { RoomData } from "./RoomData";
import { TimelineRoomData } from "./TimelineRoomData";
import { AnyWorkplaceData } from "./AnyWorkplaceData";
import { RoomActions } from "./RoomActions";
import { MapWithMiniOfficePhoto } from "./MapWithMiniOfficePhoto";
import { UsersInOffice } from "./UsersInOffice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { ReactNode, useCallback, useMemo } from "react";
import { getReservationDataFromSlot } from "@gettactic/helpers/src/reservation/getReservationDataFromSlot";
import { getScheduleCapabilitySlot } from "@gettactic/helpers/src/reservation/getScheduleCapabilitySlot";
import { ReservationModals } from "@/features/reserve/components/modals/ReservationModals";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { useResource } from "@/features/reserve/hooks/useResource";
import { JoinButton } from "@cmp-src/components/JoinButton/JoinButton";
import { Stamp } from "lucide-react";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import {
  getDateFromSchedule,
  getTimeFromSchedule
} from "@gettactic/helpers/src/date-time/getTimeFromSchedule";
import { useWorkplaceRequestsByReservation } from "@/features/workplace-requests/hooks/useWorkplaceRequestsByReservation";
import { useWorkplaceRequestById } from "@/features/workplace-requests/hooks/useWorkplaceRequestById";
import {
  mergeReservationRequests,
  originRequestIdOf
} from "@/features/workplace-requests/lib/reservationRequests";
import { useWorkplaceRequestModal } from "@/features/dashboard/contexts/WorkplaceRequestModalContext";
import { useState } from "react";
import { RequestDetailsPage } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsPage";
import { canRespondOthers, canScheduleOthers } from "@gettactic/helpers";
import { canAccessJoinLink } from "@gettactic/helpers/src/schedules/canAccessJoinLink";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import {
  ApprovalActionBox,
  ApprovalPendingNotice
} from "@/features/workplace-requests/components/ApprovalActionBox";
import { useRespondReservation } from "@/features/workplace-requests/hooks/useRespondReservation";

export type DashboardEventDetailsProps = {
  schedule: ISchedule;
  calendarSwitch?: ReactNode;
  onCancel?: () => void;
  teamSchedule?: ReactNode;
  isOrgPrivacyEnabled: boolean;
  showTime?: boolean;
  /**
   * `timeline` lays a room's tabs out as the resource schedule does: the
   * requests tab always there, attendees as a grid of cards, amenities as
   * pills.
   */
  variant?: "default" | "timeline";
};

export const DashboardEventDetails = ({
  schedule: incomingSchedule,
  calendarSwitch,
  teamSchedule,

  onCancel,
  isOrgPrivacyEnabled,
  showTime = false,
  variant = "default"
}: DashboardEventDetailsProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openModal, isOpen: contextIsOpen } = useWorkplaceRequestModal();
  const [localSelectedRequest, setLocalSelectedRequest] =
    useState<WorkplaceRequestElement | null>(null);

  const isMobile = useIsMobile();

  const isLoading = useBooleanState();
  const checkOutModalDisclosure = useDisclosure();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { openEditModal, openCancelModal } = useReserveModal();
  const { openModal: openMapModal, closeModal: closeMapModal } =
    useMapModalContext();
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");

  const { data: scheduleUpdated } = useQuery({
    queryKey: ["schedule", incomingSchedule.id],
    queryFn: () => api.client.schedules.scheduleById(incomingSchedule.id),
    enabled: !!incomingSchedule.id
  });

  const schedule = scheduleUpdated?.result || incomingSchedule;

  // A reservation can hold several meeting rooms; the pane shows one at a time and the
  // tab bar switches between them. The primary room leads — the API returns the slots
  // unordered, so it is not necessarily the first one.
  const rooms = useMemo(() => {
    const allRooms = getScheduleRooms(schedule);
    const primaryIndex = allRooms.findIndex(
      (room) => room.id === schedule.resource.id
    );
    if (primaryIndex <= 0) return allRooms;
    return [
      allRooms[primaryIndex],
      ...allRooms.filter((_, index) => index !== primaryIndex)
    ];
  }, [schedule]);
  // The pane is reused as the agenda moves between reservations, so the pick carries
  // the schedule it was made on. Matching on the room id alone let a pick survive
  // into any other reservation that happened to hold the same room, which opened it
  // on that tab instead of on its own primary room.
  const [pickedRoom, setPickedRoom] = useState<{
    scheduleId: string;
    roomId: string;
  } | null>(null);
  const selectRoom = useCallback(
    (roomId: string) => setPickedRoom({ scheduleId: schedule.id, roomId }),
    [schedule.id]
  );
  const activeRoom =
    (pickedRoom?.scheduleId === schedule.id
      ? rooms.find((room) => room.id === pickedRoom.roomId)
      : undefined) ?? schedule.resource;
  const isMultiRoom = rooms.length > 1;

  const { resource } = useResource({ id: activeRoom.id });
  const officeId = activeRoom.office_id;
  const parentId = activeRoom.parent_id;
  const { areas: areaList } = useAreas(officeId);
  const thisOffice = authenticatedUser.offices?.byId[officeId];
  const officeName = thisOffice?.name;
  const areaName = parentId ? areaList?.byId[parentId]?.name : undefined;

  const targetSlot = schedule
    ? findCheckinTargetSlot(schedule, authenticatedUser.user?.id ?? "")
    : undefined;

  const { allowCheckIn, allowCheckOut: allowCheckOutFromSlot } =
    getReservationDataFromSlot(targetSlot as unknown as ISlot);
  // Walk-up occupancy is checked out externally via the API, never through
  // the app.
  const allowCheckOut = allowCheckOutFromSlot && !activeRoom.is_walkup;

  const link = createMapLink({
    date: dateStringToLocalTime(schedule.start),
    officeId,
    resourceId: activeRoom.id,
    floorId: activeRoom.parent_id ?? undefined,
    startTime: dateStringToLocalTime(schedule.start),
    endTime: schedule.end ? dateStringToLocalTime(schedule.end) : undefined
  });

  // Awaiting-approval box: an approver gets Approve/Reject, anyone else
  // (including the requester) gets a read-only notice. canRespondOthers is
  // the same broad, client-side permission check the reservations approval
  // queue already gates on — the backend still enforces the precise rule
  // per request. Kept above the early return below so the hook call stays
  // unconditional (schedule is already resolved by this point either way).
  const canRespond = canRespondOthers(authenticatedUser);
  const respondReservation = useRespondReservation();
  const handleRespond = (decision: "accepted" | "rejected") => {
    if (!schedule) return;
    respondReservation.mutate(
      { scheduleIds: [schedule.id], decision },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: ["schedule", schedule.id]
          })
      }
    );
  };

  // Same duplicate flow as the dashboard agenda card (AgendaDay): prefill the
  // reserve modal from the existing reservation. The incoming prop may be an
  // agenda item with true-UTC times, so always read the reservation from the
  // schedules endpoint (cached by the query above), which returns the office
  // wall-clock times the reserve flow expects.
  const handleDuplicate = useCallback(async () => {
    const { result: wallClockSchedule } = await queryClient.ensureQueryData({
      queryKey: ["schedule", incomingSchedule.id],
      queryFn: () => api.client.schedules.scheduleById(incomingSchedule.id)
    });
    if (!wallClockSchedule) return;

    const duplicateData = await getDuplicateReservationData({
      item: wallClockSchedule,
      user: authenticatedUser,
      isWorkplaceRequestsAllowed,
      api,
      shouldSelectResource: true,
      authenticatedUser
    });

    const defaultTab = getMapModalTab(wallClockSchedule.resource.type);

    openMapModal({
      date: wallClockSchedule.start.split("T")[0],
      officeId,
      reservationModalIsOpen: true,
      duplicateData,
      defaultTab,
      onSuccess: () => {
        closeMapModal();
      }
    });
  }, [
    queryClient,
    incomingSchedule.id,
    authenticatedUser,
    isWorkplaceRequestsAllowed,
    officeId,
    openMapModal,
    closeMapModal
  ]);

  const { allElements: filedRequests, isFetching: isLoadingExisting } =
    useWorkplaceRequestsByReservation(schedule.id, {
      enabled: !!schedule.id
    });
  // The request whose approval booked this reservation (managed rooms) is not
  // filed against it, so the by-schedule list above never returns it.
  const originRequestId = isWorkplaceRequestsAllowed
    ? originRequestIdOf(incomingSchedule)
    : undefined;
  const { data: originRequest } = useWorkplaceRequestById(
    originRequestId ?? ""
  );
  const existingRequests = useMemo(
    () => mergeReservationRequests(filedRequests, originRequest ?? undefined),
    [filedRequests, originRequest]
  );

  if (!schedule) {
    return null;
  }

  const renderAsRoom = isRoom(schedule.resource.type);

  const resourceName = schedule.resource.name;
  const meetingName = schedule.title;
  // A multi-room reservation is named after the event, never after one of its rooms —
  // the room names live in the tab bar.
  const eventTitle = isMultiRoom
    ? meetingName || `${rooms.length} Rooms`
    : renderAsRoom
      ? (meetingName ?? resourceName)
      : resourceName;

  const userSlot = schedule.slots.find(
    (slot) => slot.user.id === authenticatedUser.user?.id
  );

  // Slot capabilities are computed by the API for the requesting viewer on
  // every slot, so a viewer without a slot of their own (e.g. an admin
  // opening someone else's reservation from the timeline) still gets their
  // permissions from the reservation's primary slot.
  const capabilitySlot = getScheduleCapabilitySlot(
    schedule,
    authenticatedUser.user?.id
  );
  const { canEdit, canRespondCancel } = capabilitySlot
    ? getReservationDataFromSlot(capabilitySlot)
    : { canEdit: false, canRespondCancel: false };

  // Function to handle cancel action
  const handleCancel = useCallback(async () => {
    if (!schedule) return;
    await openCancelModal([schedule]);
  }, [schedule, openCancelModal]);

  const handleEdit = useCallback(() => {
    if (!schedule) return;

    openEditModal(schedule, true);
  }, [schedule, openEditModal]);
  const resourceMediaUrl = resource ? getResourceCoverUrl(resource) : undefined;

  const handleCheckInClick = () => {
    /*if (!onCheckin) return;
    const slot = reservation.slots[0];
    onCheckin(slot.resource.id, reservation.id, slot.id);*/
  };

  const handleCheckOutClick = () => {
    /*if (!onCheckout) return;
    const slot = reservation.slots[0];
    onCheckout(slot.resource.id, reservation.id, slot.id);
    setIsCheckOutModalOpen(false);*/
  };

  return (
    <div
      className={cn(
        "mb-4 flex w-full flex-col items-center justify-center",
        // The timeline's modal has no room to spare around its content.
        variant !== "timeline" && "md:mt-3.5 md:mb-16"
      )}
    >
      <div className="flex h-full w-full flex-col">
        <div className="relative hidden flex-row items-center justify-between pb-4 md:flex">
          <div>
            {/* The schedules endpoint returns office wall-clock times carrying a
                fake "Z", so the date and times are read as written. Converting
                them into the office timezone would shift them by its offset. */}
            <DayDate
              apiDate={getDateFromSchedule(schedule.start)}
              className="v2-header-1"
              dateClassNAme={"text-[32px] font-bold font-mona"}
            />
            {showTime && schedule.start && schedule.end && (
              <p className="mt-1 text-neutral-600 text-sm">
                {getTimeFromSchedule(schedule.start)}
                {" - "}
                {getTimeFromSchedule(schedule.end)}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <JoinButton
              iconOnly={true}
              onlineMeeting={schedule.online_meeting ?? null}
              // DashboardEventDetails is also reused by TimelineEventModal for
              // arbitrary room reservations (e.g. Outlook-synced meetings), so
              // we must gate the Teams "Join" link to organizer/attendee/admin.
              hasAccess={canAccessJoinLink(schedule, authenticatedUser)}
              onRefetch={() => {
                queryClient.invalidateQueries({
                  queryKey: ["schedule", schedule.id]
                });
                queryClient.invalidateQueries({
                  queryKey: ["schedules"]
                });
              }}
              wrapper={(children, icon, props) => (
                <Button
                  variant="outlinePale"
                  {...props}
                  className={cn("flex items-center px-3", props.className)}
                >
                  {icon}
                  {children}
                </Button>
              )}
            />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outlinePale"
                  className="px-3"
                  disabled={!canEdit && !canRespondCancel}
                >
                  <EditIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                  >
                    Manage
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate();
                  }}
                >
                  Duplicate
                </DropdownMenuItem>
                {canRespondCancel && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    variant="danger"
                  >
                    Remove
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                router.push(link);
              }}
            >
              <ViewDeskIcon />
              Locate
            </Button>
          </div>
        </div>
        {schedule.is_pending && (
          <div className="px-2.5 pb-4 md:px-0">
            {canRespond ? (
              <ApprovalActionBox
                icon={Stamp}
                rejectLabel="Deny"
                description="This reservation is awaiting your approval."
                isLoading={respondReservation.isPending}
                onApprove={() => handleRespond("accepted")}
                onReject={() => handleRespond("rejected")}
              />
            ) : (
              <ApprovalPendingNotice description="This reservation is awaiting approval." />
            )}
          </div>
        )}
        {isMultiRoom && (
          <div className="px-2.5 pb-4 md:px-0">
            <EventRoomTabs
              rooms={rooms}
              selectedRoomId={activeRoom.id}
              onSelectRoom={selectRoom}
              timeLabel={`${getTimeFromSchedule(schedule.start)} → ${
                schedule.end ? getTimeFromSchedule(schedule.end) : "…"
              }`}
            />
          </div>
        )}
        {schedule.start && (
          <MapWithMiniOfficePhoto
            resourceId={activeRoom.id}
            officeId={activeRoom.office_id}
            parentId={activeRoom.parent_id ?? ""}
            startDate={schedule.start.split("T")[0]}
            resourceImage={resourceMediaUrl}
          />
        )}
        <div className="flex flex-row items-center justify-between px-2.5 pt-4 md:px-0 md:pt-[30px]">
          <div className="flex flex-col justify-between min-w-0 flex-1">
            <div className="font-bold text-lg">{eventTitle}</div>
            <ResourceLocation
              location={officeName || "Location"}
              floor={areaName || "Floor"}
              maxLength={renderAsRoom ? 45 : Number.POSITIVE_INFINITY}
              allowWrap={!renderAsRoom}
            />
          </div>
          {renderAsRoom && userSlot && (
            <RoomActions
              schedule={schedule}
              userSlot={userSlot}
              remoteEnabled={true}
            />
          )}
        </div>

        {renderAsRoom && (
          <div className={variant === "timeline" ? "pt-5" : "px-0 py-14"}>
            {variant === "timeline" ? (
              <TimelineRoomData
                schedule={schedule}
                resource={resource}
                workplaceRequests={existingRequests}
              />
            ) : (
              <RoomData
                schedule={schedule}
                resource={resource}
                workplaceRequests={existingRequests}
              />
            )}
          </div>
        )}
        <div className="px-2.5 md:px-0">
          {teamSchedule ? (
            <div className="flex flex-col gap-4">
              {renderAsRoom ? null : (
                <AnyWorkplaceData
                  schedule={schedule}
                  officeId={officeId}
                  workplaceRequests={existingRequests}
                  teamSchedule={teamSchedule}
                  onRequestClick={
                    contextIsOpen
                      ? openModal
                      : (request) => setLocalSelectedRequest(request)
                  }
                  isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                />
              )}
            </div>
          ) : renderAsRoom ? null : (
            <AnyWorkplaceData
              schedule={schedule}
              officeId={officeId}
              workplaceRequests={existingRequests}
              onRequestClick={
                contextIsOpen
                  ? openModal
                  : (request) => setLocalSelectedRequest(request)
              }
              isOrgPrivacyEnabled={isOrgPrivacyEnabled}
            />
          )}
        </div>
        <div className={"flex gap-2.5 px-2.5 md:hidden"}>
          <div className={cn("w-full", isMobile && "invisible mb-12")}>
            <Button
              className={"w-full"}
              variant={"secondary"}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
          {(allowCheckIn || allowCheckOut) && (
            <div className={"w-full"}>
              {allowCheckIn && (
                <Button
                  className={"w-full"}
                  onClick={handleCheckInClick}
                  disabled={isLoading.state}
                >
                  {isLoading.state ? "Processing..." : "Check In"}
                </Button>
              )}
              {allowCheckOut && (
                <Button
                  className={"w-full"}
                  onClick={() => checkOutModalDisclosure.open()}
                  disabled={isLoading.state}
                  variant="secondary"
                >
                  {isLoading.state ? "Processing..." : "Check Out"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <ReservationModals />
      {checkOutModalDisclosure.isOpen && (
        <ConfirmModal
          title="Check out"
          message="Are you sure you want to check out?"
          onConfirm={handleCheckOutClick}
          onOpenChange={checkOutModalDisclosure.onOpenChange}
          onCancel={checkOutModalDisclosure.close}
          isOpen={checkOutModalDisclosure.isOpen}
        />
      )}
      {localSelectedRequest && (
        <RequestDetailsPage
          dontTouchUrl={true}
          requestId={localSelectedRequest.id}
          isAdmin={canScheduleOthers(authenticatedUser)}
          onRequestUpdate={(updatedRequest: WorkplaceRequestElement) => {
            setLocalSelectedRequest(updatedRequest);
          }}
          isPageMode={false}
          initialRequest={localSelectedRequest}
          onClose={() => setLocalSelectedRequest(null)}
          schedule={schedule}
        />
      )}
    </div>
  );
};
